import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  getAllProjectsSummary,
  addProjectTeam,
  getProjectById,
  updateProjectTeam,
} from "../../../api/projectApi";
import { getUsersSummary } from "../../../api/api";
import "./AddProjectTeam.css";
import "../common.css";
import Preloader from "../../Preloader/Preloader.js";

const AddProjectTeam = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [sections, setSections] = useState([
    { id: Date.now(), section: null, leadSpecialist: null, specialists: [] },
  ]);
  const [loading, setLoading] = useState(true);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [users, setUsers] = useState([]);

  const projectSections = [
    { value: "ЭП", label: "ЭП" },
    { value: "АС", label: "АС" },
    { value: "АН", label: "АН" },
    { value: "КЖ", label: "КЖ" },
    { value: "ОВ", label: "ОВ" },
    { value: "ВК", label: "ВК" },
    { value: "СС", label: "СС" },
    { value: "ЭМ", label: "ЭМ" },
    { value: "ГП", label: "ГП" },
    { value: "ЭО", label: "ЭО" },
    { value: "ЭО1", label: "ЭО1" },
    { value: "ПС", label: "ПС" },
    { value: "СД", label: "СД" },
    { value: "Управление проектом", label: "Управление проектом" },
    { value: "BIM", label: "BIM" },
    { value: "Default", label: "Default" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData, usersData] = await Promise.all([
          getAllProjectsSummary(),
          getUsersSummary({ isActive: true, pageSize: 200 }),
        ]);

        if (Array.isArray(projectsData) && projectsData.length > 0) {
          setProjects(
            projectsData.map((project) => ({
              value: project.id,
              label: project.name,
            }))
          );
        } else {
          console.error("Получены некорректные данные проектов:", projectsData);
          setProjects([]);
        }

        if (usersData.users && usersData.users.length > 0) {
          setUsers(
            usersData.users.map((user) => ({
              value: user.id,
              label: `${user.surname} ${user.name}`,
            }))
          );
        } else {
          console.error(
            "Получены некорректные данные пользователей:",
            usersData
          );
          setUsers([]);
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
        setProjects([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Автоматически выбираем проект, если передан projectId через URL
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.value === parseInt(projectId, 10));
      if (project) {
        handleProjectChange(project);
      }
    }
  }, [projectId, projects]);

  // Дополнительная логика для загрузки проекта напрямую, если projectId передан
  useEffect(() => {
    if (projectId && !selectedProject && users.length > 0) {
      const loadProjectDirectly = async () => {
        try {
          setLoading(true);
          const projectData = await getProjectById(projectId);
          setProjectData(projectData);

          if (projectData.teams && projectData.teams.length > 0) {
            const formattedSections = projectData.teams.map((team) => ({
              id: team.id,
              section: projectSections.find(
                (s) => s.value === team.project_section
              ) || { value: team.project_section, label: team.project_section },
              leadSpecialist:
                team.lead_specialist && team.lead_specialist[0]
                  ? users.find(
                      (u) => u.value === team.lead_specialist[0].id
                    ) || {
                      value: team.lead_specialist[0].id,
                      label: `${team.lead_specialist[0].surname || ""} ${
                        team.lead_specialist[0].name || ""
                      }`,
                    }
                  : null,
              specialists: team.specialists
                ? team.specialists.map(
                    (specialist) =>
                      users.find((u) => u.value === specialist.id) || {
                        value: specialist.id,
                        label: `${specialist.surname || ""} ${
                          specialist.name || ""
                        }`,
                      }
                  )
                : [],
            }));
            setSections(formattedSections);
          } else {
            setSections([
              {
                id: Date.now(),
                section: null,
                leadSpecialist: null,
                specialists: [],
              },
            ]);
          }
        } catch (error) {
          console.error("Ошибка при загрузке данных проекта:", error);
          setSections([
            {
              id: Date.now(),
              section: null,
              leadSpecialist: null,
              specialists: [],
            },
          ]);
        } finally {
          setLoading(false);
        }
      };

      loadProjectDirectly();
    }
  }, [projectId, users.length, selectedProject]);

  const handleProjectChange = async (selectedOption) => {
    setSelectedProject(selectedOption);
    setLoading(true);

    try {
      const projectData = await getProjectById(selectedOption.value);
      setProjectData(projectData);

      if (projectData.teams && projectData.teams.length > 0) {
        const formattedSections = projectData.teams.map((team) => ({
          id: team.id,
          section: projectSections.find(
            (s) => s.value === team.project_section
          ) || { value: team.project_section, label: team.project_section },
          leadSpecialist:
            team.lead_specialist && team.lead_specialist[0]
              ? users.find((u) => u.value === team.lead_specialist[0].id) || {
                  value: team.lead_specialist[0].id,
                  label: `${team.lead_specialist[0].surname || ""} ${
                    team.lead_specialist[0].name || ""
                  }`,
                }
              : null,
          specialists: team.specialists
            ? team.specialists.map(
                (specialist) =>
                  users.find((u) => u.value === specialist.id) || {
                    value: specialist.id,
                    label: `${specialist.surname || ""} ${
                      specialist.name || ""
                    }`,
                  }
              )
            : [],
        }));
        setSections(formattedSections);
      } else {
        setSections([
          {
            id: Date.now(),
            section: null,
            leadSpecialist: null,
            specialists: [],
          },
        ]);
      }
    } catch (error) {
      console.error("Ошибка при загрузке данных проекта:", error);
      setSections([
        {
          id: Date.now(),
          section: null,
          leadSpecialist: null,
          specialists: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (id, field, value) => {
    const newSections = sections.map((section) =>
      section.id === id ? { ...section, [field]: value } : section
    );
    setSections(newSections);
  };

  const addSection = () => {
    setSections([
      ...sections,
      {
        id: Date.now(),
        section: null,
        leadSpecialist: null,
        specialists: [],
      },
    ]);
  };

  const removeSection = (id) => {
    if (sections.length > 1) {
      setSections(sections.filter((section) => section.id !== id));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const currentProjectId = projectId || selectedProject?.value;
      if (
        !currentProjectId ||
        sections.some((section) => !section.section || !section.leadSpecialist)
      ) {
        throw new Error("Пожалуйста, заполните все обязательные поля");
      }

      const teamData = sections.map((section) => ({
        project_id: currentProjectId,
        project_section: section.section.value,
        lead_specialist: [section.leadSpecialist.value],
        specialists: section.specialists.map((specialist) => specialist.value),
        ...(section.id && !isNaN(section.id) && section.id < 1000000
          ? { team_id: section.id }
          : {}),
      }));

      console.log("Отправляемые данные:", teamData); // Для отладки

      for (const data of teamData) {
        if (!data.team_id) {
          const result = await addProjectTeam(data);
          console.log("Команда проекта успешно добавлена:", result);
        } else {
          const result = await updateProjectTeam(data);
          console.log("Команда проекта успешно обновлена:", result);
        }
      }

      setShowSuccessPopup(true);

      setTimeout(() => {
        setShowSuccessPopup(false);
        navigate("/projects/list");
      }, 3000);
    } catch (error) {
      console.error("Ошибка при обновлении команды проекта:", error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "392px",
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided, state) => ({
      ...provided,
      borderRadius: "5px",
    }),
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="project-form-container">
      <form
        className="project-form add-project-team registration"
        onSubmit={handleSubmit}
      >
        <ul className="add-project-team__inputs registration__inputs">
          {false && (
            <li className="add-project-team__container registration__inputs-container"></li>
          )}

          {projectId && projectData && (
            <li className="add-project-team__container registration__inputs-container">
              <label className="add-project-team__label registration__label">
                Редактирование команды проекта
              </label>
              <Select
                options={projects}
                value={
                  projects.find((p) => p.value === parseInt(projectId)) || null
                }
                onChange={(opt) => {
                  if (!opt) return;
                  setSelectedProject(opt);
                  navigate(`/projects/teams/${opt.value}`);
                }}
                placeholder="Выберите проект"
                styles={customStyles}
              />
            </li>
          )}

          {((projectId && projectData) || (selectedProject && projectData)) && (
            <>
              <li className="add-project-team__container registration__inputs-container">
                <label className="add-project-team__label registration__label">
                  ГИП
                </label>
                <input
                  className="add-project-team__input registration__input"
                  value={
                    projectData.chief
                      ? `${projectData.chief.name} ${projectData.chief.surname}`
                      : "Не назначен"
                  }
                  disabled
                />
              </li>
              {sections.map((section, index) => (
                <div key={section.id} className="section-group">
                  {sections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSection(section.id)}
                      className="remove-section-button"
                      aria-label="Удалить секцию"
                    >
                      &times;
                    </button>
                  )}
                  <li className="add-project-team__container registration__inputs-container">
                    <label className="add-project-team__label registration__label">
                      Раздел проекта
                    </label>
                    <Select
                      options={projectSections}
                      onChange={(selectedOption) =>
                        handleSectionChange(
                          section.id,
                          "section",
                          selectedOption
                        )
                      }
                      value={section.section}
                      placeholder="Выберите раздел проекта"
                      styles={customStyles}
                    />
                  </li>
                  <li className="add-project-team__container registration__inputs-container">
                    <label className="add-project-team__label registration__label">
                      Лидер раздела
                    </label>
                    <Select
                      options={users}
                      onChange={(selectedOption) =>
                        handleSectionChange(
                          section.id,
                          "leadSpecialist",
                          selectedOption
                        )
                      }
                      value={section.leadSpecialist}
                      placeholder="Выберите лидера раздела"
                      styles={customStyles}
                    />
                  </li>
                  <li className="add-project-team__container registration__inputs-container">
                    <label className="add-project-team__label registration__label">
                      Специалисты раздела
                    </label>
                    <Select
                      options={users}
                      isMulti
                      onChange={(selectedOptions) =>
                        handleSectionChange(
                          section.id,
                          "specialists",
                          selectedOptions
                        )
                      }
                      value={section.specialists}
                      placeholder="Выберите специалистов"
                      styles={customStyles}
                    />
                  </li>
                </div>
              ))}
              <div className="section-buttons">
                <button
                  type="button"
                  onClick={addSection}
                  className="add-button newreport__button button-add button-active"
                >
                  +
                </button>
              </div>
              <div className="add-project-team__buttons">
                <button
                  type="submit"
                  className="add-project-team__button-submit registration__button-submit"
                >
                  Сохранить изменения
                </button>
              </div>
            </>
          )}
        </ul>

        {showSuccessPopup && (
          <div className="popup-overlay">
            <div className="popup">
              <div className="popup-icon"></div>
              <p className="popup-text">Команда проекта успешно обновлена</p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default AddProjectTeam;
